/**
 * Relais mémoire du composeur pendant dock → assistant (#1588).
 *
 * Le dock est volontairement démonté AVANT la navigation pour qu'un seul chat
 * existe. Son composeur reste pourtant éditable pendant le tour : l'avocat peut
 * préparer la suite et joindre un acte. Ce petit store transporte ce brouillon
 * vers le composeur du même fil, puis l'oublie dès que celui-ci l'a copié.
 * Rien n'est persisté ni envoyé automatiquement.
 */

import { create } from 'zustand';

export interface BrouillonComposeur {
  readonly content: string;
  readonly files: readonly File[];
}

interface BrouillonComposeurEnRelais extends BrouillonComposeur {
  readonly conversationId: string;
}

interface BrouillonComposeurRelaisStore {
  readonly pending: BrouillonComposeurEnRelais | null;
  readonly preserver: (conversationId: string, brouillon: BrouillonComposeur) => void;
  readonly consommer: (conversationId: string) => void;
  readonly oublier: () => void;
}

const useBrouillonComposeurRelaisStore = create<BrouillonComposeurRelaisStore>()((set) => ({
  pending: null,
  preserver: (conversationId, brouillon) => set({
    pending: brouillon.content.length === 0 && brouillon.files.length === 0
      ? null
      : { conversationId, content: brouillon.content, files: [...brouillon.files] },
  }),
  consommer: (conversationId) => set((state) => (
    state.pending?.conversationId === conversationId ? { pending: null } : state
  )),
  oublier: () => set({ pending: null }),
}));

/** La clé du relais vers `/assistant` sans conversation : un premier message refusé y revient. */
export const BROUILLON_DE_LA_PAGE_VIERGE = 'page-vierge';

export function preserverBrouillonComposeurRelais(
  conversationId: string,
  brouillon: BrouillonComposeur,
): void {
  useBrouillonComposeurRelaisStore.getState().preserver(conversationId, brouillon);
}

export function lireBrouillonComposeurRelais(conversationId: string | null): BrouillonComposeur | null {
  const pending = useBrouillonComposeurRelaisStore.getState().pending;
  if (!conversationId || pending?.conversationId !== conversationId) return null;
  return { content: pending.content, files: pending.files };
}

export function consommerBrouillonComposeurRelais(conversationId: string): void {
  useBrouillonComposeurRelaisStore.getState().consommer(conversationId);
}

export function oublierLesBrouillonsComposeurRelais(): void {
  useBrouillonComposeurRelaisStore.getState().oublier();
}

/** Le brouillon à poser dans le fil actuellement rendu par `/assistant`. */
export function useBrouillonComposeurRelais(conversationId: string | null): BrouillonComposeur | null {
  return useBrouillonComposeurRelaisStore((state) => {
    const pending = state.pending;
    if (!conversationId || pending?.conversationId !== conversationId) return null;
    return pending;
  });
}
